import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient } from '../utils/apiClient';
import './Workspace.css';

const Workspace = () => {
  const [workspaceData, setWorkspaceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [currentPath, setCurrentPath] = useState('');
  const [selectedPath, setSelectedPath] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploadFiles, setUploadFiles] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [folderContents, setFolderContents] = useState({});
  const [contextMenu, setContextMenu] = useState(null);
  const [clipboard, setClipboard] = useState({ type: null, items: [] });
  const [dragState, setDragState] = useState({ isDragging: false, draggedItem: null });
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const loadWorkspaceData = useCallback(async (path = '') => {
    try {
      setLoading(true);
      const url = path ? `/workspace?path=${encodeURIComponent(path)}` : '/workspace';
      const response = await apiClient.get(url);
      setWorkspaceData(response.data);
      setCurrentPath(path);
      updateBreadcrumbs(path);
      
      // Also load root folder contents for the Workspace expandable folder
      if (path === '') {
        setFolderContents(prev => ({
          ...prev,
          '': response.data.files
        }));
      }
    } catch (error) {
      console.error('Error loading workspace data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkspaceData();
  }, [loadWorkspaceData]);

  // Debug context menu state
  useEffect(() => {
    console.log('Context menu state changed:', contextMenu);
  }, [contextMenu]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenu && !event.target.closest('.context-menu')) {
        closeContextMenu();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu]);

  const updateBreadcrumbs = (path) => {
    if (!path) {
      setBreadcrumbs([{ name: 'Workspace', path: '' }]);
      return;
    }
    
    const pathParts = path.split('/').filter(part => part);
    const crumbs = [{ name: 'Workspace', path: '' }];
    
    let currentPath = '';
    pathParts.forEach(part => {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      crumbs.push({ name: part, path: currentPath });
    });
    
    setBreadcrumbs(crumbs);
  };

  const handleFileSelect = (path) => {
    setSelectedPath(path);
  };

  const handleFolderSelect = (path) => {
    setSelectedPath(path);
    setCurrentPath(path);
    updateBreadcrumbs(path);
  };

  const handleFolderClick = (path) => {
    loadWorkspaceData(path);
    setSelectedPath('');
  };

  const handleBreadcrumbClick = (path) => {
    loadWorkspaceData(path);
    setSelectedPath('');
  };

  const loadFolderContents = async (folderPath) => {
    try {
      const response = await apiClient.get(`/workspace?path=${encodeURIComponent(folderPath)}`);
      setFolderContents(prev => ({
        ...prev,
        [folderPath]: response.data.files
      }));
    } catch (error) {
      console.error('Error loading folder contents:', error);
    }
  };

  const toggleFolderExpansion = async (folderPath) => {
    const newExpandedFolders = new Set(expandedFolders);
    
    if (expandedFolders.has(folderPath)) {
      // Collapse folder
      newExpandedFolders.delete(folderPath);
      setExpandedFolders(newExpandedFolders);
    } else {
      // Expand folder
      newExpandedFolders.add(folderPath);
      setExpandedFolders(newExpandedFolders);
      
      // Load folder contents if not already loaded
      if (!folderContents[folderPath]) {
        if (folderPath === '') {
          // For root directory, use the current workspace data
          setFolderContents(prev => ({
            ...prev,
            '': workspaceData?.files || []
          }));
        } else {
          await loadFolderContents(folderPath);
        }
      }
    }
  };

  const isFolderExpanded = (folderPath) => {
    return expandedFolders.has(folderPath);
  };

  const handleDownload = async (filePath) => {
    try {
      const response = await apiClient.get(`/workspace/download?path=${encodeURIComponent(filePath)}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filePath.split('/').pop());
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    }
  };

  const handleDelete = useCallback(async (path) => {
    if (!window.confirm(`Are you sure you want to delete "${path}"?`)) {
      return;
    }

    try {
      console.log('Deleting item with path:', path);
      await apiClient.delete(`/workspace?path=${encodeURIComponent(path)}`);
      
      // Refresh the current view
      loadWorkspaceData();
      
      // If we're in a subfolder, also refresh the folder contents
      if (currentPath !== '') {
        loadFolderContents(currentPath);
      }
      
      if (selectedPath === path) {
        setSelectedPath('');
      }
    } catch (error) {
      console.error('Error deleting file/folder:', error);
      alert('Failed to delete file/folder');
    }
  }, [selectedPath, loadWorkspaceData, currentPath]);

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;

    try {
      setUploading(true);
      const formData = new FormData();
      uploadFiles.forEach(file => {
        formData.append('files', file);
      });
      formData.append('path', currentPath);

      await apiClient.post('/workspace/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadFiles([]);
      setShowUploadModal(false);
      
      // Refresh the current view
      loadWorkspaceData();
      
      // If we're in a subfolder, also refresh the folder contents
      if (currentPath !== '') {
        loadFolderContents(currentPath);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const folderPath = currentPath ? `${currentPath}/${newFolderName}` : newFolderName;
      await apiClient.post('/workspace/folder', { path: folderPath });
      setNewFolderName('');
      setShowCreateFolderModal(false);
      
      // Refresh the current view
      loadWorkspaceData();
      
      // If we're in a subfolder, also refresh the folder contents
      if (currentPath !== '') {
        loadFolderContents(currentPath);
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      alert('Failed to create folder');
    }
  };

  const handleFileInputChange = (e) => {
    setUploadFiles(Array.from(e.target.files));
  };

  // Drag and drop handlers for upload modal
  const handleUploadDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleUploadDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleUploadDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    setUploadFiles(prevFiles => [...prevFiles, ...files]);
  };

  const removeFile = (index) => {
    setUploadFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const clearAllFiles = () => {
    setUploadFiles([]);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return 'Today at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 2) {
      return 'Yesterday at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays <= 7) {
      return date.toLocaleDateString([], { weekday: 'short' }) + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { 
        day: 'numeric', 
        month: 'short', 
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
      });
    }
  };

  const getFileIcon = (item) => {
    if (item.type === 'folder') {
      return '📁';
    }
    
    const extension = item.name.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'md':
        return '📝';
      case 'txt':
        return '📄';
      case 'pdf':
        return '📕';
      case 'doc':
      case 'docx':
        return '📘';
      case 'xls':
      case 'xlsx':
        return '📊';
      case 'ppt':
      case 'pptx':
        return '📋';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'svg':
        return '🖼️';
      case 'mp4':
      case 'avi':
      case 'mov':
        return '🎬';
      case 'mp3':
      case 'wav':
        return '🎵';
      case 'zip':
      case 'rar':
      case '7z':
        return '🗜️';
      case 'json':
        return '🔧';
      case 'js':
      case 'jsx':
        return '⚛️';
      case 'css':
        return '🎨';
      case 'html':
        return '🌐';
      case 'py':
        return '🐍';
      case 'java':
        return '☕';
      case 'cpp':
      case 'c':
        return '⚙️';
      default:
        return '📄';
    }
  };

  const getFileKind = (item) => {
    if (item.type === 'folder') {
      return 'Folder';
    }
    
    const extension = item.name.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'md':
        return 'Markdown File';
      case 'txt':
        return 'Text Document';
      case 'pdf':
        return 'PDF Document';
      case 'doc':
      case 'docx':
        return 'Word Document';
      case 'xls':
      case 'xlsx':
        return 'Excel Spreadsheet';
      case 'ppt':
      case 'pptx':
        return 'PowerPoint Presentation';
      case 'jpg':
      case 'jpeg':
        return 'JPEG Image';
      case 'png':
        return 'PNG Image';
      case 'gif':
        return 'GIF Image';
      case 'svg':
        return 'SVG Image';
      case 'mp4':
        return 'MP4 Video';
      case 'avi':
        return 'AVI Video';
      case 'mov':
        return 'QuickTime Movie';
      case 'mp3':
        return 'MP3 Audio';
      case 'wav':
        return 'WAV Audio';
      case 'zip':
        return 'ZIP Archive';
      case 'rar':
        return 'RAR Archive';
      case '7z':
        return '7-Zip Archive';
      case 'json':
        return 'JSON File';
      case 'js':
        return 'JavaScript File';
      case 'jsx':
        return 'React Component';
      case 'css':
        return 'CSS Stylesheet';
      case 'html':
        return 'HTML Document';
      case 'py':
        return 'Python Script';
      case 'java':
        return 'Java Source';
      case 'cpp':
        return 'C++ Source';
      case 'c':
        return 'C Source';
      default:
        return 'Document';
    }
  };

  // Context Menu Functions
  const handleContextMenu = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Context menu triggered for:', item);
    
    // Position the context menu at the cursor position (top-left aligned)
    const menuHeight = 200; // Approximate height of the context menu
    const menuWidth = 150; // Approximate width of the context menu
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    let x = e.clientX;
    let y = e.clientY + 30; // Move menu a little bit lower
    
    // Adjust if menu would go off screen vertically
    if (y + menuHeight > viewportHeight) {
      y = viewportHeight - menuHeight - 5; // Show above viewport bottom
    }
    
    // Adjust if menu would go off screen horizontally
    if (x + menuWidth > viewportWidth) {
      x = viewportWidth - menuWidth - 5; // Show to the left of viewport right
    }
    
    // Ensure menu doesn't go off the left or top edges
    if (x < 0) x = 5;
    if (y < 0) y = 5;
    
    setContextMenu({
      x: x,
      y: y,
      item: item
    });
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  const handleCopy = useCallback((item) => {
    setClipboard({ type: 'copy', items: [item] });
    closeContextMenu();
  }, []);

  const handleCut = useCallback((item) => {
    setClipboard({ type: 'cut', items: [item] });
    console.log('Item cut to clipboard:', item);
    console.log('Clipboard state set to:', { type: 'cut', items: [item] });
    closeContextMenu();
  }, []);

  const handlePaste = useCallback(async (targetPath) => {
    if (!clipboard.items.length) {
      console.log('No items in clipboard to paste');
      return;
    }

    console.log('Pasting items:', clipboard.items, 'to path:', targetPath);

    try {
      for (const item of clipboard.items) {
        const sourcePath = item.path;
        const fileName = item.name;
        const targetFilePath = targetPath ? `${targetPath}/${fileName}` : fileName;

        console.log('Pasting item:', sourcePath, 'to:', targetFilePath, 'type:', clipboard.type);

        if (clipboard.type === 'copy') {
          // Copy operation - create a copy
          await apiClient.post('/workspace/copy', {
            source: sourcePath,
            destination: targetFilePath
          });
        } else if (clipboard.type === 'cut') {
          // Move operation - move the file
          await apiClient.post('/workspace/move', {
            source: sourcePath,
            destination: targetFilePath
          });
        }
      }
      
      // Clear clipboard after paste
      setClipboard({ type: null, items: [] });
      
      // Refresh the current view
      loadWorkspaceData();
      
      // If we're in a subfolder, also refresh the folder contents
      if (currentPath !== '') {
        loadFolderContents(currentPath);
      }
      
      // If this was a cut operation, we need to refresh the source location too
      if (clipboard.type === 'cut') {
        // Find the source folder for each cut item
        for (const item of clipboard.items) {
          const sourceFolder = item.path.substring(0, item.path.lastIndexOf('/'));
          if (sourceFolder !== currentPath && sourceFolder !== '') {
            console.log('Refreshing source folder:', sourceFolder);
            loadFolderContents(sourceFolder);
          }
        }
      }
    } catch (error) {
      console.error('Error pasting items:', error);
      alert('Failed to paste items');
    }
    
    closeContextMenu();
  }, [clipboard, currentPath, loadWorkspaceData]);

  const handleRename = async (item) => {
    const newName = prompt('Enter new name:', item.name);
    if (newName && newName !== item.name) {
      try {
        await apiClient.post('/workspace/rename', {
          path: item.path,
          newName: newName
        });
        
        // Refresh the current view
        loadWorkspaceData();
        
        // If we're in a subfolder, also refresh the folder contents
        if (currentPath !== '') {
          loadFolderContents(currentPath);
        }
        
        // Clear selection if the renamed item was selected
        if (selectedPath === item.path) {
          setSelectedPath('');
        }
      } catch (error) {
        console.error('Error renaming item:', error);
        alert('Failed to rename item');
      }
    }
    closeContextMenu();
  };

  // Drag and Drop Functions
  const handleDragStart = (e, item) => {
    setDragState({ isDragging: true, draggedItem: item });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.path);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetPath) => {
    e.preventDefault();
    
    if (!dragState.draggedItem) return;

    const sourcePath = dragState.draggedItem.path;
    const fileName = dragState.draggedItem.name;
    const targetFilePath = targetPath ? `${targetPath}/${fileName}` : fileName;

    try {
      await apiClient.post('/workspace/move', {
        source: sourcePath,
        destination: targetFilePath
      });
      
      // Refresh the current view
      loadWorkspaceData();
      
      // If we're in a subfolder, also refresh the folder contents
      if (currentPath !== '') {
        loadFolderContents(currentPath);
      }
    } catch (error) {
      console.error('Error moving item:', error);
      alert('Failed to move item');
    }

    setDragState({ isDragging: false, draggedItem: null });
  };

  const findItemByPath = useCallback((path) => {
    if (!workspaceData?.files) return null;
    return workspaceData.files.find(item => item.path === path);
  }, [workspaceData]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'c':
            if (selectedPath) {
              e.preventDefault();
              const item = findItemByPath(selectedPath);
              if (item) handleCopy(item);
            }
            break;
          case 'x':
            if (selectedPath) {
              e.preventDefault();
              const item = findItemByPath(selectedPath);
              if (item) handleCut(item);
            }
            break;
          case 'v':
            e.preventDefault();
            handlePaste(currentPath);
            break;
          case 'a':
            e.preventDefault();
            // Select all items
            break;
          default:
            break;
        }
      }
      
      if (e.key === 'Delete' && selectedPath) {
        e.preventDefault();
        handleDelete(selectedPath);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedPath, currentPath, clipboard, findItemByPath, handleCopy, handleCut, handlePaste, handleDelete]);

  const renderFinderList = (items, level = 0) => {
    return items.map((item, index) => {
      const isExpanded = isFolderExpanded(item.path);
      const hasContents = folderContents[item.path] && folderContents[item.path].length > 0;
      const isLoading = expandedFolders.has(item.path) && !folderContents[item.path];
      
      
      return (
        <div key={index}>
          <div 
            className={`finder-row ${selectedPath === item.path ? 'selected' : ''} ${dragState.isDragging && dragState.draggedItem?.path === item.path ? 'dragging' : ''} ${clipboard.type === 'cut' && clipboard.items.some(cutItem => cutItem.path === item.path) ? 'cut-item' : ''}`}
            onClick={() => item.type === 'file' ? handleFileSelect(item.path) : handleFolderSelect(item.path)}
            onDoubleClick={() => item.type === 'folder' && handleFolderClick(item.path)}
            onContextMenu={(e) => handleContextMenu(e, item)}
            draggable
            onDragStart={(e) => handleDragStart(e, item)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, item.type === 'folder' ? item.path : currentPath)}
            style={{ paddingLeft: `${level * 20 + 16}px` }}
          >
            <div className="finder-cell finder-name">
              {item.type === 'folder' && (
                <button
                  className="disclosure-triangle"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFolderExpansion(item.path);
                  }}
                >
                  {isExpanded ? '▼' : '▶'}
                </button>
              )}
              {item.type === 'file' && <span className="disclosure-spacer"></span>}
              <span className="finder-icon">{getFileIcon(item)}</span>
              <span className="finder-filename">{item.name}</span>
            </div>
            <div className="finder-cell finder-date">
              {formatDate(item.modifiedAt)}
            </div>
            <div className="finder-cell finder-size">
              {item.type === 'file' ? formatFileSize(item.size) : '--'}
            </div>
            <div className="finder-cell finder-kind">
              {getFileKind(item)}
            </div>
            <div className="finder-cell finder-actions">
              {item.type === 'file' && (
                <button 
                  className="finder-action-btn download-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(item.path);
                  }}
                  title="Download"
                >
                  Download
                </button>
              )}
              {clipboard.items.length > 0 && (
                <button 
                  className="finder-action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePaste(item.type === 'folder' ? item.path : currentPath);
                  }}
                  title="Paste"
                >
                  📋
                </button>
              )}
              <button 
                className="finder-action-btn delete-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(item.path);
                }}
                title="Delete"
              >
                Delete
              </button>
            </div>
          </div>
          
          {/* Render expanded folder contents */}
          {item.type === 'folder' && isExpanded && (
            <div className="folder-contents">
              {isLoading ? (
                <div className="folder-loading">
                  <span>Loading...</span>
                </div>
              ) : hasContents ? (
                renderFinderList(folderContents[item.path], level + 1)
              ) : (
                <div className="empty-folder">
                  <span>This folder is empty</span>
                </div>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="workspace-loading">
        <div className="loading-spinner"></div>
        <p>Loading workspace...</p>
      </div>
    );
  }

  return (
    <div className="workspace-container">
      <div className="workspace-header">
        <h2>Personal Workspace</h2>
        <div className="workspace-actions">
          <button 
            className="workspace-action-btn"
            onClick={() => setShowUploadModal(true)}
          >
            Upload Files
          </button>
          <button 
            className="workspace-action-btn"
            onClick={() => setShowCreateFolderModal(true)}
          >
            New Folder
          </button>
          <button 
            className="workspace-action-btn"
            onClick={() => loadWorkspaceData(currentPath)}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-container">
        <div className="breadcrumb">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              <button
                className={`breadcrumb-item ${index === breadcrumbs.length - 1 ? 'active' : ''}`}
                onClick={() => handleBreadcrumbClick(crumb.path)}
                disabled={index === breadcrumbs.length - 1}
              >
                {crumb.name}
              </button>
              {index < breadcrumbs.length - 1 && <span className="breadcrumb-separator">/</span>}
            </React.Fragment>
          ))}
        </div>
        <div className="current-path">
          {currentPath ? `Current: ${currentPath}` : 'Current: Workspace'}
        </div>
      </div>

      <div className="workspace-content">
        <div className="finder-container">
          <div className="finder-header">
            <div className="finder-cell finder-name">
              <span className="finder-header-text">Name</span>
            </div>
            <div className="finder-cell finder-date">
              <span className="finder-header-text">Date Modified</span>
            </div>
            <div className="finder-cell finder-size">
              <span className="finder-header-text">Size</span>
            </div>
            <div className="finder-cell finder-kind">
              <span className="finder-header-text">Kind</span>
            </div>
            <div className="finder-cell finder-actions">
              <span className="finder-header-text">Actions</span>
            </div>
          </div>
          <div className="finder-body">
            {/* Workspace Root Folder Entry */}
            <div 
              className={`finder-row ${selectedPath === '' ? 'selected' : ''}`}
              onClick={() => handleFolderSelect('')}
              onContextMenu={(e) => handleContextMenu(e, { path: '', name: 'Workspace', type: 'folder' })}
              style={{ paddingLeft: '16px' }}
            >
              <div className="finder-cell finder-name">
                <button
                  className="disclosure-triangle"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFolderExpansion('');
                  }}
                >
                  {isFolderExpanded('') ? '▼' : '▶'}
                </button>
                <span className="finder-icon">📁</span>
                <span className="finder-filename">Workspace</span>
              </div>
              <div className="finder-cell finder-date">
                --
              </div>
              <div className="finder-cell finder-size">
                --
              </div>
              <div className="finder-cell finder-kind">
                Folder
              </div>
              <div className="finder-cell finder-actions">
                <span className="finder-action-btn" title="Root Directory">
                  🏠
                </span>
              </div>
            </div>
            
            {/* Render expanded Workspace contents */}
            {isFolderExpanded('') && (
              <div className="folder-contents">
                {workspaceData?.files && workspaceData.files.length > 0 ? (
                  renderFinderList(workspaceData.files, 1)
                ) : (
                  <div className="empty-folder">
                    <span>This folder is empty</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <div>
                <h3>Upload Files</h3>
                <p className="modal-subtitle">
                  Files will be uploaded to: <strong>{currentPath ? currentPath : 'Workspace'}</strong>
                </p>
              </div>
              <button 
                className="modal-close"
                onClick={() => setShowUploadModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div 
                className={`upload-area ${isDragOver ? 'drag-over' : ''}`}
                onDragOver={handleUploadDragOver}
                onDragLeave={handleUploadDragLeave}
                onDrop={handleUploadDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileInputChange}
                  style={{ display: 'none' }}
                />
                
                <div className="upload-zone">
                  <div className="upload-icon">📁</div>
                  <h4>Drop files here or click to browse</h4>
                  <p>You can drag and drop multiple files at once</p>
                  <button 
                    className="workspace-action-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Browse Files
                  </button>
                </div>
                
                {uploadFiles.length > 0 && (
                  <div className="selected-files">
                    <div className="selected-files-header">
                      <h4>Selected Files ({uploadFiles.length})</h4>
                      <button 
                        className="clear-files-btn"
                        onClick={clearAllFiles}
                        title="Clear all files"
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="file-list">
                      {uploadFiles.map((file, index) => (
                        <div key={index} className="file-item">
                          <div className="file-info">
                            <span className="file-icon">{getFileIcon({ name: file.name, type: 'file' })}</span>
                            <div className="file-details">
                              <span className="file-name">{file.name}</span>
                              <span className="file-size">{formatFileSize(file.size)}</span>
                            </div>
                          </div>
                          <button 
                            className="remove-file-btn"
                            onClick={() => removeFile(index)}
                            title="Remove file"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowUploadModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleUpload}
                disabled={uploading || uploadFiles.length === 0}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateFolderModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <div>
                <h3>Create New Folder</h3>
                <p className="modal-subtitle">
                  Folder will be created in: <strong>{currentPath ? currentPath : 'Workspace'}</strong>
                </p>
              </div>
              <button 
                className="modal-close"
                onClick={() => setShowCreateFolderModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Folder Name:</label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Enter folder name"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowCreateFolderModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim()}
              >
                Create Folder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div 
          className="context-menu"
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 1000
          }}
          onClick={closeContextMenu}
        >
          <div className="context-menu-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="context-menu-item"
              onClick={() => handleCopy(contextMenu.item)}
            >
              Copy
            </button>
            <button 
              className="context-menu-item"
              onClick={() => handleCut(contextMenu.item)}
            >
              Cut
            </button>
            {clipboard.items.length > 0 && (
              <button 
                className="context-menu-item"
                onClick={() => handlePaste(contextMenu.item.type === 'folder' ? contextMenu.item.path : currentPath)}
              >
                Paste
              </button>
            )}
            <button 
              className="context-menu-item"
              onClick={() => handleRename(contextMenu.item)}
            >
              Rename
            </button>
            <hr className="context-menu-separator" />
            <button 
              className="context-menu-item context-menu-danger"
              onClick={() => {
                handleDelete(contextMenu.item.path);
                closeContextMenu();
              }}
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Workspace;
